import { useNavigate } from '@tanstack/react-router';
import { addWeeks, startOfDay } from 'date-fns';

import { useConversionEventsMutation } from '@/api/hooks/mutations/use-conversion-events-mutation';
import {
  AvailabilityCalendar,
  type AvailabilityCalendarFormData,
} from '@/components/AvailabilityCalendar';
import { NavigationBar } from '@/components/NavigationBar';
import StepperCard from '@/components/common/StepperCard.tsx';
import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useSessionSave } from '@/hooks/use-session-save';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { trackTelehealthBookingReservationSubmitted } from '@/lib/analytics';
import {
  DEFAULT_TELEHEALTH_CONTACT_TYPE,
  NEW_PATIENT_VISIT_DURATION_IN_MINUTES,
  TELEHEALTH_CONTACT_TYPE,
} from '@/lib/constants';
import { useGetPersistedContact } from '@/views/hooks/use-get-persisted-contact.ts';

const PRIORITY_APPOINTMENT_TYPE_ID = import.meta.env
  .VITE_HEALTHIE_APPOINTMENT_TYPE_NEW_PATIENT_VISIT_ID;
const PRIORITY_CONTACT_TYPE = DEFAULT_TELEHEALTH_CONTACT_TYPE;
const SUPPLEMENTAL_APPOINTMENT_TYPE_ID = import.meta.env
  .VITE_HEALTHIE_APPOINTMENT_TYPE_VANTAGE_VISIT_ID;
const SUPPLEMENTAL_CONTACT_TYPE = TELEHEALTH_CONTACT_TYPE.PHONE;

export const ScheduleAppointment = () => {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();

  const startDate = startOfDay(new Date());
  const endDate = startOfDay(addWeeks(startDate, 2));
  const patientStateOfResidence = useSessionStore().contactState;

  const { representativeAuthorizedForMedicalDecisions } = useSessionStore();

  const conversionEventsMutation = useConversionEventsMutation();
  const contact = useGetPersistedContact();

  const handleOnSubmit = async (data: AvailabilityCalendarFormData) => {
    if (!data.selectedSlot) return;

    conversionEventsMutation.mutate({ ...contact, eventName: 'Schedule' });

    useSessionStore.getState().updateState({
      schedulingSelectedSlot: data.selectedSlot,
      schedulingSelectedProviderId: data.selectedProviderId ?? '',
      schedulingSelectedContactType: data.selectedContactType ?? '',
      schedulingSelectedAppointmentTypeId: data.selectedAppointmentTypeId ?? '',
    });

    trackTelehealthBookingReservationSubmitted({
      th_appointment_datetime: data.selectedSlot,
    });

    saveSession();

    navigate({ to: '/verify-profile/phone-number' });
  };

  return (
    <section>
      <NavigationBar onBack={() => navigate({ to: '/appointment/intro' })} backLabel="Back" />
      <StepperCard currentStep={4} totalSteps={4}>
        <CardHeader>
          <CardTitle>
            Schedule a {NEW_PATIENT_VISIT_DURATION_IN_MINUTES} min clinician visit
          </CardTitle>

          {representativeAuthorizedForMedicalDecisions === 'no' && (
            <CardDescription>
              <p className="text-orchid-600 font-medium">Important note</p>
              <p>Patient must be present for Telehealth call</p>
            </CardDescription>
          )}
        </CardHeader>

        <AvailabilityCalendar
          startDate={startDate}
          endDate={endDate}
          licensedInState={patientStateOfResidence}
          onSubmit={handleOnSubmit}
          priorityAppointmentTypeId={PRIORITY_APPOINTMENT_TYPE_ID}
          priorityContactType={PRIORITY_CONTACT_TYPE}
          supplementalAppointmentTypeId={SUPPLEMENTAL_APPOINTMENT_TYPE_ID}
          supplementalContactType={SUPPLEMENTAL_CONTACT_TYPE}
        />
      </StepperCard>
    </section>
  );
};
